import React, {
    forwardRef,
    useContext,
    useState,
    useEffect,
    useRef,
    useCallback,
    useMemo,
    useImperativeHandle,
} from 'react';
import { useLocation } from 'react-router-dom';
import { AlertBanner, CardSurface, Divider, InputTextArea, Label, TagGeneral, Tabs, Text, sparkToast, useReadOnly } from '@spark';
import { useCommercialModels } from '@/shared/hooks/useCommercialModels';
import { extractExtraParamsFromPrompt } from '@/features/datasets/components/DataSelection/ExtraParamsUtils';
import LoadingTaskData from '@/features/tasks/components/LoadingTaskData';
import { useAuth } from '@/features/auth/AuthContext';
import ResponseInterpreterSection from './ResponseInterpreterSection';
import ResponseParserInspectLogicModal from './ResponseParserInspectLogicModal';
import { getFunctionSignature, TAB } from './utils';
import { TaskType, isGenerativeTask } from '@/shared/domain/taskTypes';
import { data_from_server_post } from '@/shared/http/client';
import { isParseableJsonStructure, parseJsonLenient } from '@/shared/utils/helpers';
import AdditionalFieldsRail from './AdditionalFieldsRail';
import ModelSelection from './ModelSelection';
import NoProvidersAlert from './NoProvidersAlert';
import OutputFormatField from './OutputFormatField';
import PromptTemplateEditor from './PromptTemplateEditor';
import SpecificAITab from './SpecificAITab';
import TemplatePreviewToggle from './TemplatePreviewToggle';
import ToggleFieldLabel from './ToggleFieldLabel';
import ComparisonSwitch from './ComparisonSwitch';
import { GlobalContext } from '@/app/providers/globalState';
import { usePlaygroundApi } from '@/features/playground/hooks/usePlaygroundApi';
import { useHasNoCommercialProviders } from '@/features/model-setup/hooks/useHasNoCommercialProviders';
import { useTeacherPromptAutoSave } from '@/features/model-setup/hooks/useTeacherPromptAutoSave';
import { useStudentOverridesAutoSave } from '@/features/model-setup/hooks/useStudentOverridesAutoSave';
import { useEvaluationNormalizationAutoSave } from '@/features/model-setup/hooks/useEvaluationNormalizationAutoSave';
import { useParserFunctionAutoValidate } from '@/features/model-setup/hooks/useParserFunctionAutoValidate';
import { useParserCodeState } from '@/features/model-setup/hooks/useParserCodeState';
import {
    DEFAULT_EVALUATION_NORMALIZATION,
    evaluationLabelNamesFromUsecase,
    evaluationNormalizationFromUsecase,
} from '@/features/model-setup/utils/evaluationNormalization';
import { insertTokenIntoTemplate } from '@/features/model-setup/utils/promptTemplate';
import { specificAIDiffBadge } from '@/features/model-setup/utils/specificAIOverrides';
import {
    useTeacherSamplePreview,
    COMMERCIAL_MODEL_REQUIRED_MESSAGE,
    RUN_PROMPT_REQUIRED_MESSAGE,
    RUN_MODEL_AND_PROMPT_REQUIRED_MESSAGE,
} from '@/features/model-setup/hooks/useTeacherSamplePreview';
import { buildTeacherModelDisplay } from '@/features/model-setup/utils/teacherModelUtils';
import { studentOverridesFromUsecase } from '@/features/model-setup/utils/saveStudentOverrides';
import {
    canInferJsonSchemaFromTeacherSetup,
    inferJsonSchemaFromResponse,
    SCHEMA_INFER_NEEDS_OBJECT_MESSAGE,
} from '@/features/model-setup/utils/inferJsonSchema';
import {
    resolveTeacherSchemaForRequest,
    teacherModelSupportsJsonSchema,
} from '@/features/model-setup/utils/supportsJsonSchema';
import './ModelSetupPage.css';

const PROMPT_REQUIRED_MESSAGE = 'Please enter a prompt.';
/** Keep the manual "Infer from response" spinner visible long enough to register. */
const SCHEMA_MANUAL_INFER_MIN_MS = 300;

const ModelSetup = forwardRef(({
    embeddedInTaskDrawer = false,
    isDrawerFullWidth = false,
} = {}, ref) => {
    const { role } = useAuth();
    const { readOnly } = useReadOnly();
    const location = useLocation();
    const {
        getCurrentUsecase,
        setCurrentUsecaseId,
        updatePromptAndTeacher,
        updateParserFunction,
        updateParserValidationStatus,
        updateUsecase,
        progressStatus
    } = useContext(GlobalContext);

    const [localUsecase, setLocalUsecase] = useState(null);
    const [teacherConfig, setTeacherConfig] = useState({ provider: 'openai', model: null });
    const teacher = teacherConfig.model;
    const selectedProvider = teacherConfig.provider;
    const [prompt, setPrompt] = useState(null);
    const [promptError, setPromptError] = useState(null);
    const [systemPrompt, setSystemPrompt] = useState('');
    const [teacherSchema, setTeacherSchema] = useState('');
    const [teacherSchemaEnabled, setTeacherSchemaEnabled] = useState(false);
    // Commercial model / SpecificAI model tab (generative tasks only).
    const [activeTab, setActiveTab] = useState('commercial');
    // Prompt editor Template / Preview switch.
    const [promptMode, setPromptMode] = useState('template');
    // "System Prompt" accordion toggle (off = no system prompt is sent).
    const [systemPromptOn, setSystemPromptOn] = useState(false);
    // Additional fields rail toggle.
    const [fieldsRailEnabled, setFieldsRailEnabled] = useState(false);
    // "When evaluating" output-cleanup flags (shared by both tabs).
    const [evaluationNormalization, setEvaluationNormalization] = useState(
        DEFAULT_EVALUATION_NORMALIZATION
    );
    // Student override fields; ``null`` = "Same as commercial", ``''`` =
    // "Disable", any other text = "Customize".
    const [studentOverrides, setStudentOverrides] = useState({
        systemPrompt: null,
        userPrompt: null,
        schema: null,
    });
    const {
        requestExample,
        setRequestExample,
        rawResponse,
        setRawResponse,
        extraParamsValues,
        setExtraParamsValues,
        shuffleLoading,
        generatingResponse,
        runValidationError,
        setRunValidationError,
        handleShuffleExample,
        generateResponse,
        ensureTeacherResponse,
        batchNotice,
    } = useTeacherSamplePreview({
        usecase: localUsecase,
        teacher,
        provider: selectedProvider,
        prompt,
        systemPrompt,
        teacherSchema,
        schemaEnabled: teacherSchemaEnabled,
    });
    const [initialRequestExample, setInitialRequestExample] = useState("");
    const [rawResponseLoading, setRawResponseLoading] = useState(true);
    const [validationLoading, setValidationLoading] = useState(false);
    const [validationResult, setValidationResult] = useState(null);
    const [rawResponseError, setRawResponseError] = useState(null);
    const [showResponseParser, setShowResponseParser] = useState(false);
    const [extraParamsOptions, setExtraParamsOptions] = useState({});
    const hasInitializedRef = useRef(false);
    const [responseType, setResponseType] = useState('json');
    const [isParserDrawerOpen, setParserDrawerOpen] = useState(false);
    const [schemaManualInferLoading, setSchemaManualInferLoading] = useState(false);
    const autoDetectedResponseRef = useRef(null);

    const setParserValidationFailed = useCallback((result) => {
        const errorMessage =
            result?.error_message ||
            result?.message ||
            'Response parser validation failed. Update the prompt or parser function, then validate again.';
        updateParserValidationStatus?.(true, errorMessage);
    }, [updateParserValidationStatus]);

    const api = usePlaygroundApi();
    const { availableProviders, isLoading: isProvidersLoading } = useCommercialModels();
    // Bedrock catalog as ``[{id, vendor}]`` derived from the single
    // ``availableProviders`` source. Used by ``buildTeacherModelDisplay``
    // to look up the selected teacher model when the provider is bedrock.
    const bedrockCatalog = availableProviders?.bedrock?.models || [];
    const { hasNoProviders, classificationOnlyConfigured } = useHasNoCommercialProviders(
        localUsecase?.task_type
    );
    const teacherSupportsJsonSchema = useMemo(
        () => teacherModelSupportsJsonSchema({
            provider: selectedProvider,
            modelId: teacher,
            availableProviders,
        }),
        [selectedProvider, teacher, availableProviders],
    );

    // Commercial model lists are provided via useCommercialModels()

    // Auto-save the teacher model + prompt while the user edits, so leaving
    // this page (back navigation, drawer close, tab change) never loses
    // their latest input. The full setup screen edits one selected subtask
    // at a time; sibling prompts must remain independent even during
    // first-time setup.
    const {
        autosaveError,
        canSave,
        siblingsToPropagate,
        saveTeacherSelectionNow,
        flushSave,
        clearAutosaveError,
    } = useTeacherPromptAutoSave({
        usecase: localUsecase,
        prompt,
        systemPrompt,
        teacherSchema,
        teacherConfig,
        bedrockCatalog,
        enabled: !!localUsecase,
        propagateToSiblings: false,
        role,
        onSaved: ({
            llm_usecase_id,
            raw_prompt,
            teacher_model,
            teacher_provider,
            teacher_model_display,
            teacher_system_prompt,
            teacher_schema,
        }) => {
            // Keep the page's working copy in sync so downstream reads see
            // the just-saved values without waiting for a context refetch.
            setLocalUsecase((prev) =>
                prev && prev._id === llm_usecase_id
                    ? {
                        ...prev,
                        prompt: raw_prompt,
                        teacher_model: teacher_model || '',
                        teacher_provider,
                        teacher_model_display,
                        teacher_system_prompt,
                        teacher_schema,
                    }
                    : prev
            );
        },
    });

    const isGenerative = isGenerativeTask(localUsecase?.task_type);
    const evaluationLabelNames = useMemo(
        () => evaluationLabelNamesFromUsecase(localUsecase),
        [localUsecase]
    );

    // Auto-save the student overrides while typing (same debounce/flush
    // pattern as teacher+prompt). Global context is updated inside the hook;
    // keep the page working copy in sync via onSaved.
    const {
        autosaveError: studentAutosaveError,
        clearAutosaveError: clearStudentAutosaveError,
        flushSave: flushStudentSave,
    } = useStudentOverridesAutoSave({
        usecase: localUsecase,
        overrides: studentOverrides,
        enabled: !!localUsecase && isGenerative,
        role,
        onSaved: ({ llm_usecase_id, ...savedFields }) => {
            setLocalUsecase((prev) =>
                prev && prev._id === llm_usecase_id
                    ? { ...prev, ...savedFields }
                    : prev
            );
        },
    });

    // "When evaluating" flags autosave (same debounce/flush pattern).
    const {
        autosaveError: normalizationAutosaveError,
        clearAutosaveError: clearNormalizationAutosaveError,
        flushSave: flushNormalizationSave,
    } = useEvaluationNormalizationAutoSave({
        usecase: localUsecase,
        normalization: evaluationNormalization,
        enabled: !!localUsecase,
        role,
        onSaved: ({ llm_usecase_id, ...savedFields }) => {
            setLocalUsecase((prev) =>
                prev && prev._id === llm_usecase_id
                    ? { ...prev, ...savedFields }
                    : prev
            );
        },
    });

    const handleNormalizationChange = useCallback((partial) => {
        setEvaluationNormalization((prev) => ({ ...prev, ...partial }));
    }, []);

    const handleTeacherConfigChange = useCallback((nextTeacherConfig) => {
        setTeacherConfig(nextTeacherConfig);
        if (nextTeacherConfig?.model) {
            setRunValidationError((prev) => (
                prev === COMMERCIAL_MODEL_REQUIRED_MESSAGE
                || prev === RUN_MODEL_AND_PROMPT_REQUIRED_MESSAGE
                    ? null
                    : prev
            ));
            void saveTeacherSelectionNow(nextTeacherConfig);
        }
    }, [saveTeacherSelectionNow]);

    // Wizard Next (and any other host) can call this to require a prompt
    // before continuing. Uses live textarea state, not the last autosave.
    useImperativeHandle(ref, () => ({
        validateBeforeNext: async () => {
            if (!canSave) {
                setPromptError(PROMPT_REQUIRED_MESSAGE);
                // Detailed error toast, bottom-right — same as the wizard's
                // Business Goals step (Figma 3423-15453).
                sparkToast.error('Prompt required', {
                    content: PROMPT_REQUIRED_MESSAGE,
                    detailed: true,
                    duration: 5000,
                });
                return false;
            }
            setPromptError(null);
            try {
                clearAutosaveError();
                clearStudentAutosaveError();
                clearNormalizationAutosaveError();
                await Promise.all([flushSave(), flushStudentSave(), flushNormalizationSave()]);
                return true;
            } catch {
                return false;
            }
        },
        flushPendingSaves: async () => {
            clearAutosaveError();
            clearStudentAutosaveError();
            clearNormalizationAutosaveError();
            await Promise.all([flushSave(), flushStudentSave(), flushNormalizationSave()]);
        },
    }), [
        canSave,
        flushSave,
        flushStudentSave,
        flushNormalizationSave,
        clearAutosaveError,
        clearStudentAutosaveError,
        clearNormalizationAutosaveError,
    ]);

    useEffect(() => {
        const contextUsecase = getCurrentUsecase();
        if (!contextUsecase) return;

        // Only re-hydrate the working copy when the *active subtask*
        // actually changed (mount, route swap, drawer opens on a different
        // sibling). Re-syncing on every context refresh would clobber the
        // textarea while the user is mid-edit and an auto-save just
        // landed: ``getCurrentUsecase`` is memoized with ``allUsecases``,
        // so each ``updateUsecase`` from the auto-save flips its identity
        // and would otherwise re-run this effect.
        if (localUsecase?._id === contextUsecase._id) {
            return;
        }

        setLocalUsecase(contextUsecase);
        setPrompt(contextUsecase.prompt);
        setSystemPrompt(contextUsecase.teacher_system_prompt || '');
        setSystemPromptOn(Boolean((contextUsecase.teacher_system_prompt || '').trim()));
        setTeacherSchema(contextUsecase.teacher_schema || '');
        setTeacherSchemaEnabled(Boolean((contextUsecase.teacher_schema || '').trim()));
        setActiveTab('commercial');
        setPromptMode('template');
        setFieldsRailEnabled(
            Object.keys(extractExtraParamsFromPrompt(contextUsecase.prompt || '')).length > 0
        );
        setEvaluationNormalization(evaluationNormalizationFromUsecase(contextUsecase));
        // Summarization historically defaults to NOT sending the prompts to
        // the student model (the old Train page checkbox default). Surface
        // that as an explicit "Disable" selection for never-configured tasks.
        const hydratedOverrides = studentOverridesFromUsecase(contextUsecase);
        if (contextUsecase.task_type === TaskType.summarization) {
            if (hydratedOverrides.systemPrompt == null) hydratedOverrides.systemPrompt = '';
            if (hydratedOverrides.userPrompt == null) hydratedOverrides.userPrompt = '';
        }
        setStudentOverrides(hydratedOverrides);
        setTeacherConfig({
            provider: contextUsecase.teacher_provider || 'openai',
            model: contextUsecase.teacher_model || null,
        });
        // If there's already a parser function, show the response parser section
        if (contextUsecase.parser_function) {
            setShowResponseParser(true);
        }
        // Set comparison mode from database
        if (contextUsecase.comparison_mode !== undefined) {
            setShowResponseParser(contextUsecase.comparison_mode);
        }
        rawResponseFetchedRef.current = false;
        setRawResponse('');
        setRawResponseLoading(true);
        setSchemaManualInferLoading(false);
        hasInitializedRef.current = true;
    }, [getCurrentUsecase, localUsecase?._id]);

    useEffect(() => {
        if (!localUsecase) {
            const urlParams = new URLSearchParams(location.search);
            const usecaseId = urlParams.get('usecaseId');

            if (usecaseId) {
                setCurrentUsecaseId(usecaseId);
            }
        }
    }, [location, localUsecase, setCurrentUsecaseId]);

    const rawResponseFetchedRef = useRef(false);

    const fetchData = async () => {
        if (localUsecase && !rawResponseFetchedRef.current) {
            rawResponseFetchedRef.current = true;
            setRawResponseError(null);
            await api.rawResponses(
                {
                    client_id: localUsecase.client_id,
                    llm_usecase_id: localUsecase._id,
                    first_index: 0,
                    num_records: 1,
                    force_generate: true,
                },
                (response) => {
                    if (response.data && response.data.length > 0) {
                        const extra_params = response.data[0]?.response?.extra_params || {};
                        setExtraParamsOptions(extra_params);
                        const initialExtraParamsValues = {};
                        Object.keys(extra_params).forEach(key => {
                            if (Object.keys(extractExtraParamsFromPrompt(prompt)).includes(key)) {
                                initialExtraParamsValues[key] = extra_params[key];
                            }
                        });
                        setExtraParamsValues(initialExtraParamsValues);
                    }
                    if (!response.success) {
                        // Check if this is an API key error
                        if (response.detail && response.detail.includes("At least one OpenAI API key must be set in settings")) {
                            console.log('You need at least one commercial provider credential in Settings to load responses.');
                        } else if (response.detail && response.detail.trim()) {
                            sparkToast.error(response.detail);
                        }
                    }
                    setRawResponseLoading(false);
                    if (response && response.data && response.data.length > 0) {
                        setRawResponse(response.data[0]?.value || "");
                        const requestExample = response.data[0]?.request_example || "";
                        setInitialRequestExample(requestExample);
                        setRequestExample(requestExample);
                    }
                },
            );
        }
    };

    useEffect(() => {
        if (initialRequestExample) {
            return;
        }

        // Check if we should re-fetch due to dataset upload completion
        if (localUsecase && Array.isArray(localUsecase.datasets) && localUsecase.datasets.length > 0) {
            const hasAnyProgressUpdate = localUsecase.datasets.some((datasetName) => !!progressStatus?.[datasetName]);

            // If any dataset upload has a progress update and no initial request example, allow re-fetching
            if (hasAnyProgressUpdate && !initialRequestExample) {
                rawResponseFetchedRef.current = false;
                fetchData();
            }
        }
    }, [localUsecase, api, progressStatus]);


    useEffect(() => {
        fetchData();
    }, [localUsecase, api]);

    // Parser code lifecycle (load from ``parser_function``, auto-generate
    // from mapping prefill, Tab handler, full function code) — shared with
    // the Playground Inspect modal via ``useParserCodeState``.
    const {
        userCode,
        updateUserCode,
        parserPrefill,
        handleParserPrefillChange,
        parserSelectionsFromCode,
        prefillAutoCodeIfUntouched,
        handleKeyDown,
        getFullCode,
        fullParserCode,
    } = useParserCodeState({ usecase: localUsecase, setResponseType });

    useEffect(() => {
        if (!showResponseParser) {
            setParserDrawerOpen(false);
        }
    }, [showResponseParser]);

    useEffect(() => {
        if (
            localUsecase?.task_type === TaskType.classification &&
            !localUsecase.parser_function &&
            rawResponse &&
            rawResponse !== autoDetectedResponseRef.current
        ) {
            autoDetectedResponseRef.current = rawResponse;

            if (!isParseableJsonStructure(rawResponse)) {
                setResponseType('text');
            }
        }
    }, [localUsecase, rawResponse]);

    // Teacher JSON schema stays empty unless the operator pastes one or clicks
    // "Infer from response". Auto-filling from the sample would autosave and
    // constrain teacher calls without an explicit choice. Clicking Infer with
    // an empty Model Returns runs the teacher sample first (same as Run).
    const schemaInferInFlightRef = useRef(false);
    const canInferSchema = canInferJsonSchemaFromTeacherSetup({ teacher, prompt });

    const inferSchemaFromLatestSample = useCallback(async (applySchema) => {
        const startedAt = Date.now();
        const sample = await ensureTeacherResponse();
        if (!sample) return;

        const inferred = inferJsonSchemaFromResponse(sample);
        const remaining = SCHEMA_MANUAL_INFER_MIN_MS - (Date.now() - startedAt);
        if (remaining > 0) {
            await new Promise((resolve) => {
                setTimeout(resolve, remaining);
            });
        }
        if (inferred) {
            applySchema(inferred);
        } else {
            sparkToast.info(SCHEMA_INFER_NEEDS_OBJECT_MESSAGE);
        }
    }, [ensureTeacherResponse]);

    const handleInferSchemaFromResponse = useCallback(async () => {
        if (
            role === 'Viewer'
            || !teacherSupportsJsonSchema
            || !canInferSchema
            || schemaInferInFlightRef.current
        ) {
            return;
        }

        schemaInferInFlightRef.current = true;
        setSchemaManualInferLoading(true);
        try {
            await inferSchemaFromLatestSample(setTeacherSchema);
        } finally {
            schemaInferInFlightRef.current = false;
            setSchemaManualInferLoading(false);
        }
    }, [role, teacherSupportsJsonSchema, canInferSchema, inferSchemaFromLatestSample]);

    const handleOpenParserModal = () => {
        prefillAutoCodeIfUntouched();
        setParserDrawerOpen(true);
    };

    // After Next was removed, parser changes must validate+save without an
    // explicit click. ``/validate-code`` persists when valid; callbacks sync
    // the page + global usecase store so progress checks see the new parser.
    const { validating: parserAutoValidating } = useParserFunctionAutoValidate({
        enabled: showResponseParser && !!localUsecase && !readOnly,
        role,
        clientId: localUsecase?.client_id,
        llmUsecaseId: localUsecase?._id,
        taskType: localUsecase?.task_type,
        userCode,
        fullCode: fullParserCode,
        rawResponse,
        savedParserFunction: localUsecase?.parser_function || null,
        onValidated: (result, code) => {
            setValidationResult(result);
            updateParserValidationStatus?.(false, null);
            updateParserFunction(code);
            setLocalUsecase((prev) =>
                prev ? { ...prev, parser_function: code } : prev
            );
        },
        onValidationFailed: (errorOrResult) => {
            if (typeof errorOrResult === 'string') {
                setValidationResult({ is_valid: false, error_message: errorOrResult });
                setParserValidationFailed({ message: errorOrResult });
                return;
            }
            setValidationResult(errorOrResult);
            setParserValidationFailed(errorOrResult);
        },
    });

    const handleGoToSettings = () => {
        window.location.href = "/settings";
    };

    const updateComparisonMode = async (comparisonMode) => {
        if (!localUsecase) return;

        try {
            const body = {
                client_id: localUsecase.client_id,
                llm_usecase_id: localUsecase._id,
                comparison_mode: comparisonMode,
            };

            data_from_server_post(
                "update_comparison_mode",
                body,
                (result) => {
                    if (!result.success) {
                        sparkToast.error("Failed to update comparison mode");
                        // Revert the switch if the update failed
                        setShowResponseParser(!comparisonMode);
                        return;
                    }

                    // Keep both the page's working copy and the global context in sync,
                    // so navigating away and back doesn't show a stale value.
                    setLocalUsecase((prev) =>
                        prev ? { ...prev, comparison_mode: comparisonMode } : prev
                    );
                    if (localUsecase?._id) {
                        updateUsecase(localUsecase._id, { comparison_mode: comparisonMode });
                    }

                    // Compare toggle is commercial-only: on → teacher, off → none.
                    // Do not promote or preserve manual_model_predictions / all.
                    const nextOption = comparisonMode ? 'teacher' : 'none';

                    data_from_server_post(
                        "update_comparison_mode_option",
                        {
                            client_id: localUsecase.client_id,
                            llm_usecase_id: localUsecase._id,
                            comparison_mode_option: nextOption,
                        },
                        (optResult) => {
                            if (!optResult.success) {
                                sparkToast.error("Failed to update comparison mode option");
                                return;
                            }
                            setLocalUsecase((prev) =>
                                prev ? { ...prev, comparison_mode_option: nextOption } : prev
                            );
                            if (localUsecase?._id) {
                                updateUsecase(localUsecase._id, { comparison_mode_option: nextOption });
                            }
                        },
                        (optError) => {
                            sparkToast.error(optError);
                        }
                    );
                },
                (error_message) => {
                    sparkToast.error(error_message);
                    // Revert the switch if the update failed
                    setShowResponseParser(!comparisonMode);
                }
            );
        } catch (error) {
            sparkToast.error('Error updating comparison mode: ' + error.message);
            // Revert the switch if the update failed
            setShowResponseParser(!comparisonMode);
        }
    };

    const validateCode = async (isCheckOnly = false) => {
        if (!localUsecase || !teacher || !prompt) {
            sparkToast.error("You must select teacher model & prompt");
            return;
        }

        // If raw response is empty, generate one first
        if (!rawResponse) {
            if (!requestExample) {
                sparkToast.error(`No request example available to generate response`);
                return;
            }

            // Generate response first, then validate
            setValidationLoading(true);
            try {
                // Include extra params values in the request if any are set
                let modifiedRequestExample = requestExample;
                if (Object.keys(extraParamsValues).length > 0) {
                    const extraParamsData = {};
                    Object.keys(extraParamsValues).forEach(key => {
                        if (extraParamsValues[key].trim()) {
                            try {
                                extraParamsData[key] = JSON.parse(extraParamsValues[key]);
                            } catch {
                                extraParamsData[key] = extraParamsValues[key];
                            }
                        }
                    });

                    // Add extra params to the request example as JSON
                    if (Object.keys(extraParamsData).length > 0) {
                        modifiedRequestExample = JSON.stringify({
                            example: requestExample,
                            extra_params: extraParamsData
                        });
                    }
                }

                const { schema: schemaToSend, error: schemaError } = resolveTeacherSchemaForRequest({
                    schema: teacherSchema,
                    provider: selectedProvider,
                    modelId: teacher,
                    availableProviders,
                    schemaEnabled: teacherSchemaEnabled,
                });
                if (schemaError) {
                    sparkToast.error(schemaError);
                    setRunValidationError(schemaError);
                    setValidationLoading(false);
                    return;
                }

                const body = {
                    client_id: localUsecase?.client_id || "40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75",
                    llm_usecase_id: localUsecase?._id,
                    first_index: 0,
                    num_records: 1,
                    teacher_model: teacher,
                    prompt: prompt,
                    system_prompt: (systemPrompt || '').trim() || null,
                    teacher_schema: schemaToSend,
                    request_example: modifiedRequestExample,
                    provider: selectedProvider,
                };

                data_from_server_post(
                    "raw-responses",
                    body,
                    (result) => {
                        if (result.success && result.data && result.data.length > 0) {
                            const generatedResponse = result.data[0]?.value;
                            if (generatedResponse) {
                                setRawResponse(generatedResponse);
                                // Now validate with the generated response
                                performValidation(generatedResponse, null, null, isCheckOnly);
                            } else {
                                sparkToast.error('Failed to generate response. Please check your API keys.');
                                setValidationLoading(false);
                            }
                        } else {
                            sparkToast.error('Failed to generate response');
                            setValidationLoading(false);
                        }
                    },
                    (error_message) => {
                        sparkToast.error(error_message);
                        setValidationLoading(false);
                    }
                );
            } catch (error) {
                sparkToast.error('Error generating response: ' + error.message);
                setValidationLoading(false);
            }
            return;
        }

        // If raw response exists, validate directly
        performValidation(rawResponse, null, null, isCheckOnly);
    };

    const performValidation = (responseToValidate, codeBodyOverride = null, callback = null, isCheckOnly = false) => {
        try {
            setValidationLoading(true);
            setValidationResult(null);

            let fullCode;
            if (codeBodyOverride) {
                const indentedUserCode = codeBodyOverride.split('\n').map(line => {
                    return line.trim() ? TAB + line : line;
                }).join('\n');
                fullCode = getFunctionSignature(localUsecase?.task_type) + indentedUserCode;
            } else {
                fullCode = getFullCode();
            }

            // Create a modified response that includes the extra params values
            let modifiedResponse = responseToValidate;
            if (Object.keys(extraParamsValues).length > 0) {
                try {
                    const responseObj = parseJsonLenient(responseToValidate);
                    if (responseObj && responseObj.extra_params) {
                        // Update extra_params with user-provided values
                        const updatedExtraParams = { ...responseObj.extra_params };
                        Object.keys(extraParamsValues).forEach(key => {
                            if (extraParamsValues[key].trim()) {
                                // Try to parse as JSON if it looks like JSON, otherwise use as string
                                try {
                                    updatedExtraParams[key] = JSON.parse(extraParamsValues[key]);
                                } catch {
                                    updatedExtraParams[key] = extraParamsValues[key];
                                }
                            }
                        });
                        responseObj.extra_params = updatedExtraParams;
                        modifiedResponse = JSON.stringify(responseObj, null, 2);
                    }
                } catch (error) {
                    // If parsing fails, use the original response
                    console.warn('Failed to parse response for extra params modification:', error);
                }
            }

            const body = {
                "code": fullCode,
                "raw_response": modifiedResponse,
                "task_type": localUsecase?.task_type,
            };

            if (localUsecase) {
                body["client_id"] = localUsecase.client_id;
                body["llm_usecase_id"] = localUsecase._id;
            } else {
                body["client_id"] = "40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75";
            }

            // If this is just a check (not saving), we might want to use a different endpoint or just validate
            // But currently validate-code endpoint saves parser function if valid.
            // If we want to avoid side effects we might need a 'validate_only' flag on backend.
            // However, the requirement is specifically about removing calls to save_teacher_and_prompt and subsequent fetches.

            data_from_server_post(
                "validate-code",
                body,
                (result) => {
                    if (result.detail) {
                        sparkToast.error(result.detail);
                        setValidationLoading(false);
                        setParserValidationFailed({ message: result.detail });
                        if (callback) callback({ success: false, message: result.detail });
                        return;
                    }
                    setValidationResult(result);
                    setValidationLoading(false);

                    // If validation is successful — backend already saved the
                    // parser via ``/validate-code``. Always sync frontend state
                    // so progress checks / remounts see the saved function even
                    // when this was a check-only (Inspect logic) run.
                    if (result.is_valid) {
                        updateParserValidationStatus?.(false, null);
                        if (showResponseParser) {
                            updateParserFunction(fullCode);
                            setLocalUsecase((prev) =>
                                prev ? { ...prev, parser_function: fullCode } : prev
                            );
                        }
                        if (isCheckOnly) {
                            if (callback) callback(result);
                            return;
                        }

                        let finalPrompt = prompt;
                        if (finalPrompt && !finalPrompt.includes('{{example}}')) {
                            finalPrompt += ' {{example}}';
                        }

                        // Compute display string for persistence
                        const teacherDisplay = buildTeacherModelDisplay({
                            provider: selectedProvider,
                            teacherModelId: teacher,
                            bedrockCatalog,
                            fallbackDisplay: localUsecase?.teacher_model_display || null,
                        });
                        api.saveTeacherAndPrompt({
                            client_id: localUsecase?.client_id,
                            llm_usecase_id: localUsecase?._id,
                            teacher: teacher,
                            prompt: finalPrompt,
                            provider: selectedProvider,
                            teacher_display: teacherDisplay,
                            // Empty string = explicit clear; keep in sync with
                            // the page state so this save never wipes them.
                            teacher_system_prompt: (systemPrompt || '').trim(),
                            teacher_schema: (teacherSchema || '').trim(),
                        }, () => {
                            updatePromptAndTeacher(
                                finalPrompt,
                                teacher,
                                showResponseParser,
                                selectedProvider,
                                teacherDisplay
                            );
                            if (callback) callback(result);
                        });
                    } else {
                        setParserValidationFailed(result);
                        if (callback) callback(result);
                    }
                },
                (error_message) => {
                    const failedResult = {
                        success: false,
                        message: error_message
                    };
                    setValidationResult(failedResult);
                    setParserValidationFailed(failedResult);
                    setValidationLoading(false);
                    if (callback) callback(failedResult);
                }
            );
        } catch (error) {
            const failedResult = {
                success: false,
                message: error.message
            };
            setValidationResult(failedResult);
            setParserValidationFailed(failedResult);
            setValidationLoading(false);
            if (callback) callback(failedResult);
        }
    };

    const isTeacherRequired = showResponseParser;

    if (!localUsecase) {
        return <LoadingTaskData />;
    }

    const isViewer = role === 'Viewer';
    // Preview values for prompt tokens: dataset sample values, overlaid with
    // any values the operator adjusted for the compare sample run.
    const previewParamValues = { ...extraParamsOptions, ...extraParamsValues };
    const diffBadge = specificAIDiffBadge(studentOverrides);

    const commercialTabContent = (
        <>
            <section className="model-setup-section fs-unmask" data-section="base-setup">
                <Text variant="lead-medium" color="primary" as="div">
                    Base Model and prompt setup
                </Text>

                {siblingsToPropagate.length > 0 && (
                    <AlertBanner
                        feedback="info"
                        showButton={false}
                        showCloseButton={false}
                        text={
                            siblingsToPropagate.length === 1
                                ? 'This model and prompt will also be applied to the other subtask in this task. You can change each subtask independently afterwards.'
                                : `This model and prompt will also be applied to the other ${siblingsToPropagate.length} subtasks in this task. You can change each subtask independently afterwards.`
                        }
                    />
                )}

                {autosaveError && (
                    <AlertBanner
                        feedback="error"
                        showButton={false}
                        text={(
                            <div>
                                <div>Could not save model & prompt</div>
                                <div>{autosaveError}</div>
                            </div>
                        )}
                        onCloseClick={clearAutosaveError}
                    />
                )}

                <div className="model-setup-commercial-field">
                    <ModelSelection
                        clientId={localUsecase?.client_id}
                        value={teacherConfig}
                        onChange={handleTeacherConfigChange}
                        role={role}
                        required={isTeacherRequired}
                        taskType={localUsecase?.task_type}
                        showEmptyAlert={false}
                    />
                </div>
            </section>

            <section className="model-setup-section" data-section="system-prompt">
                <ToggleFieldLabel
                    label="System Prompt"
                    active={systemPromptOn}
                    disabled={readOnly || isViewer}
                    onChange={(checked) => {
                        setSystemPromptOn(checked);
                        if (!checked) setSystemPrompt('');
                    }}
                />
                {systemPromptOn && (
                    <InputTextArea
                        showLabel={false}
                        placeholder="Add system instructions for the model..."
                        value={systemPrompt}
                        onChange={(e) => setSystemPrompt(e.target.value)}
                        rows={5}
                        stretch
                        disabled={readOnly || isViewer}
                        className="model-setup-system-prompt-textarea"
                        aria-label="System prompt"
                    />
                )}
            </section>

            <section className="model-setup-section" data-section="user-prompt">
                <div className="model-setup-prompt-grid">
                    <div className="model-setup-prompt-grid__editor">
                        <div className="model-setup-prompt-grid__head fs-unmask">
                            <Label label="User Prompt" showAsterisk />
                            <TemplatePreviewToggle mode={promptMode} onChange={setPromptMode} />
                        </div>
                        <Text variant="detail-regular" color="secondary" as="div" className="fs-unmask">
                            Include the model input. Extra columns are optional.
                        </Text>
                        <PromptTemplateEditor
                            value={prompt || ''}
                            onChange={(next) => {
                                setPrompt(next);
                                if (promptError) setPromptError(null);
                                if ((next || '').trim()) {
                                    setRunValidationError((prev) => (
                                        prev === RUN_PROMPT_REQUIRED_MESSAGE
                                        || prev === RUN_MODEL_AND_PROMPT_REQUIRED_MESSAGE
                                            ? null
                                            : prev
                                    ));
                                }
                            }}
                            mode={promptMode}
                            disabled={readOnly || isViewer}
                            placeholder="Write your model prompt."
                            exampleValue={requestExample || ''}
                            paramValues={previewParamValues}
                            error={promptError}
                            ariaLabel="User prompt"
                        />
                    </div>
                    <div className="model-setup-prompt-grid__rail">
                        <AdditionalFieldsRail
                            fields={extraParamsOptions}
                            promptTemplate={prompt || ''}
                            exampleValue={requestExample || ''}
                            enabled={fieldsRailEnabled}
                            loading={rawResponseLoading}
                            onEnabledChange={setFieldsRailEnabled}
                            onInsert={(name) => setPrompt(insertTokenIntoTemplate(prompt || '', name))}
                            disabled={isViewer}
                        />
                    </div>
                </div>
            </section>

            {teacherSupportsJsonSchema && (
                <section className="model-setup-section" data-section="output-format">
                    <OutputFormatField
                        value={teacherSchema}
                        onChange={(e) => setTeacherSchema(e.target.value)}
                        enabled={teacherSchemaEnabled}
                        onEnabledChange={setTeacherSchemaEnabled}
                        role={role}
                        onInferFromPrompt={handleInferSchemaFromResponse}
                        inferDisabled={!canInferSchema}
                        inferLoading={!isViewer && schemaManualInferLoading}
                    />
                </section>
            )}

            <Divider />

            <section className="model-setup-compare-section">
                {normalizationAutosaveError && (
                    <AlertBanner
                        feedback="error"
                        showButton={false}
                        text={(
                            <div>
                                <div>Could not save evaluation settings</div>
                                <div>{normalizationAutosaveError}</div>
                            </div>
                        )}
                        onCloseClick={clearNormalizationAutosaveError}
                    />
                )}
                <ComparisonSwitch
                    showResponseParser={showResponseParser}
                    onChange={(checked) => {
                        setShowResponseParser(checked);
                        updateComparisonMode(checked);
                    }}
                    role={role}
                    readOnly={readOnly}
                >
                    <ResponseInterpreterSection
                        requestExample={requestExample}
                        setRequestExample={setRequestExample}
                        onShuffleExample={handleShuffleExample}
                        shuffleLoading={shuffleLoading}
                        responseType={responseType}
                        setResponseType={setResponseType}
                        rawResponse={rawResponse}
                        setRawResponse={setRawResponse}
                        onGenerateResponse={generateResponse}
                        generatingResponse={generatingResponse}
                        runValidationError={runValidationError}
                        onInspectLogic={handleOpenParserModal}
                        extraParamsOptions={extraParamsOptions}
                        extraParamsValues={extraParamsValues}
                        onExtraParamsValuesChange={setExtraParamsValues}
                        promptExtraParams={extractExtraParamsFromPrompt(prompt || '')}
                        rawResponseLoading={rawResponseLoading}
                        rawResponseError={rawResponseError}
                        validationResult={validationResult}
                        taskType={localUsecase?.task_type}
                        isMultilabel={localUsecase?.is_multilabel}
                        onParserPrefillChange={handleParserPrefillChange}
                        initialParserSelections={parserSelectionsFromCode}
                        userCode={userCode}
                        onUserCodeChange={updateUserCode}
                        onValidate={validateCode}
                        validationLoading={validationLoading || parserAutoValidating}
                        mappingReadOnly={readOnly}
                        batchNotice={batchNotice}
                        promptTemplate={prompt || ''}
                        normalization={evaluationNormalization}
                        onNormalizationChange={handleNormalizationChange}
                        normalizationDisabled={readOnly || isViewer}
                        labelNames={evaluationLabelNames}
                    />
                </ComparisonSwitch>
            </section>
        </>
    );

    return (
        <div
            className={`model-setup-page${embeddedInTaskDrawer ? ' model-setup-page--in-task-drawer' : ' page-content-shell'
                }${embeddedInTaskDrawer && isDrawerFullWidth ? ' model-setup-page--drawer-full-width' : ''}`}
        >
            {/* White card chrome on the task-page tab (same pattern as Model Summary). */}
            <CardSurface
                background="white"
                bordered={false}
                padding="large"
                className="model-setup-page__content"
            >
                {hasNoProviders && (
                    <div className="model-setup-page__providers-alert fs-unmask">
                        <NoProvidersAlert
                            classificationOnlyConfigured={classificationOnlyConfigured}
                        />
                    </div>
                )}

                {isGenerative && (
                    <Tabs
                        size="large"
                        className="model-setup-tabs fs-unmask"
                        items={[
                            { id: 'commercial', label: 'Commercial model' },
                            { id: 'specificai', label: 'SpecificAI model' },
                        ]}
                        activeId={activeTab}
                        onChange={setActiveTab}
                        trailing={
                            <TagGeneral
                                label={diffBadge}
                                className="model-setup-tabs__diff-tag"
                            />
                        }
                    />
                )}

                {(!isGenerative || activeTab === 'commercial') ? (
                    commercialTabContent
                ) : (
                    <SpecificAITab
                        role={role}
                        teacherValues={{
                            systemPrompt,
                            userPrompt: prompt || '',
                            schema: teacherSupportsJsonSchema ? (teacherSchema || '') : '',
                        }}
                        overrides={studentOverrides}
                        onOverridesChange={setStudentOverrides}
                        autosaveError={studentAutosaveError || normalizationAutosaveError}
                        clearAutosaveError={() => {
                            clearStudentAutosaveError();
                            clearNormalizationAutosaveError();
                        }}
                        exampleValue={requestExample || ''}
                        paramValues={previewParamValues}
                        extraParamsOptions={extraParamsOptions}
                        extraParamsLoading={rawResponseLoading}
                        normalization={evaluationNormalization}
                        onNormalizationChange={handleNormalizationChange}
                        labelNames={evaluationLabelNames}
                    />
                )}
            </CardSurface>

            <ResponseParserInspectLogicModal
                open={isParserDrawerOpen}
                onClose={() => {
                    setParserDrawerOpen(false);
                    setValidationResult(null);
                }}
                taskType={localUsecase?.task_type}
                isMultilabel={localUsecase?.is_multilabel}
                userCode={userCode}
                onCodeChange={updateUserCode}
                handleKeyDown={handleKeyDown}
                validationResult={validationResult}
                validationLoading={validationLoading || parserAutoValidating}
                onValidate={validateCode}
                teacher={teacher}
            />
        </div >
    );
});

ModelSetup.displayName = 'ModelSetup';

export default ModelSetup;
